import React, { createContext, useContext, useState, useEffect } from "react";

const BasketContext = createContext();

const BASKET_STORAGE_KEY = "tsatsky_basket";

export function BasketProvider({ children }) {
    const [basketItems, setBasketItems] = useState(() => {
        try {
            const saved = localStorage.getItem(BASKET_STORAGE_KEY);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    useEffect(() => {
        localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(basketItems));
    }, [basketItems]);

    const addToBasket = (product) => {
        setBasketItems((prev) => {
            const existingIndex = prev.findIndex(
                (item) => item.product.article === product.article
            );

            if (existingIndex >= 0) {
                const updated = [...prev];
                updated[existingIndex] = {
                    ...updated[existingIndex],
                    quantity: updated[existingIndex].quantity + 1,
                };
                return updated;
            }

            return [...prev, { product, quantity: 1 }];
        });
    };

    const removeFromBasket = (article) => {
        setBasketItems((prev) =>
            prev.filter((item) => item.product.article !== article)
        );
    };

    const updateQuantity = (article, quantity) => {
        if (quantity <= 0) {
            removeFromBasket(article);
            return;
        }

        setBasketItems((prev) =>
            prev.map((item) =>
                item.product.article === article ? { ...item, quantity } : item
            )
        );
    };

    const clearBasket = () => {
        setBasketItems([]);
    };

    const getTotalItems = () => {
        return basketItems.reduce((sum, item) => sum + item.quantity, 0);
    };

    const getTotalPrice = () => {
        return basketItems.reduce(
            (sum, item) => sum + item.product.price * item.quantity,
            0
        );
    };

    const isInBasket = (article) => {
        return basketItems.some((item) => item.product.article === article);
    };

    const value = {
        basketItems,
        addToBasket,
        removeFromBasket,
        updateQuantity,
        clearBasket,
        getTotalItems,
        getTotalPrice,
        isInBasket,
    };

    return (
        <BasketContext.Provider value={value}>
            {children}
        </BasketContext.Provider>
    );
}

export function useBasket() {
    const context = useContext(BasketContext);
    if (!context) {
        throw new Error("useBasket must be used within a BasketProvider");
    }
    return context;
}
